import React, { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AttachmentsForm from "../../../../../../../../../../components/Attachment/AttachmentsForm";
import {
	TagsFormInput,
	mapTagsToAcceptableByBackendFormat,
} from "../../../../../../../../../../components/Tag/TagsFormInput";
import {
	ContactApi,
	StockAdjustmentApi,
	StockAdjustmentItemApi,
	TagApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import CreateContactModalForm from "../../../../components/CreateContactModalForm";
import ItemsError from "../../../../components/ItemsError";
import StockAdjustmentItemsTable from "./FormItemsTable/StockAdjustmentItemsTable";

interface Props {
	stockAdjustment?: StockAdjustmentApi;
	customFieldsConfig?: CustomFieldTemplateApi[];
	fetchStockAdjustment?: () => void;
}

const StockAdjustmentForm: FC<Props> = ({ stockAdjustment, customFieldsConfig, fetchStockAdjustment }) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<StockAdjustmentApi>({
		criteriaMode: "all",
		defaultValues: {
			...stockAdjustment,
			adjusted_at: stockAdjustment?.adjusted_at ?? formatDateToString(new Date()),
			items: !stockAdjustment?.items
				? [
						{
							position: 0,
							type: "INCOME",
						} as StockAdjustmentItemApi,
				  ]
				: [
						...stockAdjustment.items,
						{
							position: stockAdjustment.items.length,
							type: "INCOME",
						},
				  ],
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], stockAdjustment?.custom_fields || []),
		},
	});
	const {
		handleSubmit,
		formState,
		register,
		formState: { errors },
		control,
		setValue,
		setError,
		watch,
	} = form;
	const [loading, setLoading] = useState<boolean>(false);
	const [showContactModal, setShowContactModal] = useState<boolean>(false);
	const [newContact, setNewContact] = useState<ContactApi>();
	const [contact, setContact] = useState<ContactApi>(stockAdjustment?.contact as ContactApi);
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const [formErrors, setFormErrors] = useState<ApiError[]>([]);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onProcessData = (data: StockAdjustmentApi) => {
		data.items = [
			...data.items.filter(
				(item) =>
					item?.volume ||
					item?.product?.name ||
					item?.quantity ||
					(item?.price_net_money?.amount && item?.type !== "EXPENSE")
			),
		];

		data.items.forEach((item) => {
			if (
				(item?.type === "EXPENSE" && item?.quantity > 0) ||
				(item.type === "INCOME" && item?.quantity && item?.quantity < 0)
			) {
				item.quantity = item.quantity * -1;
			}
		});

		if (data.tags) data.tags = data.tags.map((t) => ({ ...t, id: t.id < 0 ? undefined : t.id } as TagApi));

		return data;
	};

	const onSubmit = handleSubmit(async (data: StockAdjustmentApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			if (data.tags) data.tags = mapTagsToAcceptableByBackendFormat(data.tags);
			let res;
			const newData = onProcessData(data);

			if (stockAdjustment?.id) {
				const params = { include: "all" };
				res = await api.organization().updateStockAdjustment(newData, params);
			} else {
				res = await api.organization().createStockAdjustment(data);
			}
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			history.push(`/${organization.id}/documents/stock_adjustments/${res.id}`);
		} catch (err) {
			handleError.form(err, setError, addFlash);
			const exceptionErrors = handleException(err);
			setFormErrors([...exceptionErrors]);
		}
		setLoading(false);
	});

	const onSearchContacts = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getContactSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onSearchStocks = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getStockSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onCreateContact = (value?: string) => {
		setNewContact({ name: value } as ContactApi);
		setShowContactModal(true);
	};

	const onSaveContact = (contact: ContactApi) => {
		setValue("contact_id", contact.id);
		setContact(contact);
		setShowContactModal(false);
	};

	const inputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const isStockInputDisabled = () => {
		if (!watch("id")) return false;
		if (!watch("status")) return false;
		return watch("status") !== "DRAFT" && watch("status") !== "VOID";
	};

	return (
		<FormDirty
			key="stock-adjustment-form"
			onSubmit={(e) => {
				onSubmit(e);
			}}
			formState={formState}
			noValidate
			loading={loading}
		>
			<h5>{t("common.word.basic_info")}</h5>
			<fieldset className="form-group">
				<div className="row">
					<div className="col-lg-10">
						<div className="row">
							<div className="col-md-6">
								<FormInput
									label={t("common.word.reference_number")}
									register={register}
									name="reference_number"
									errors={errors}
								/>
							</div>
							<div className="col-md-6">
								<FormDatePicker
									errors={errors}
									control={control}
									name="adjusted_at"
									label={t("modules.stock_adjustment.field.adjusted_at.title")}
									customValidationConfig={inputValidationConfig}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-md-6">
								<FormSelectGroup
									control={control}
									loadOptions={onSearchContacts}
									label={`${t("common.word.contact")}`}
									name="contact_id"
									getOptionValue={(opt) => opt.id}
									defaultValue={{
										label: contact?.name,
										id: contact?.id,
									}}
									onCreateOption={onCreateContact}
									path={`/${organization.id}/contacts/`}
									errors={errors}
									data-testid="contact_id"
								/>
								{showContactModal && (
									<CreateContactModalForm
										isShown={showContactModal}
										value={newContact?.name || ""}
										onHide={() => setShowContactModal(false)}
										onSave={(data) => onSaveContact(data)}
									/>
								)}
							</div>
							<div className="col-md-6">
								<FormSelectGroup
									control={control}
									loadOptions={onSearchStocks}
									label={t("common.word.stock")}
									name="stock_id"
									isClearable={false}
									getOptionValue={(opt) => opt.id}
									defaultValue={{
										label: stockAdjustment?.stock?.name,
										id: stockAdjustment?.stock?.id,
									}}
									errors={errors}
									customValidationConfig={inputValidationConfig}
									data-testid="stock_id"
									disabled={isStockInputDisabled()}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-md-6">
								<FormInput
									label={t("common.word.comment.title")}
									register={register}
									name="comment"
									errors={errors}
								/>
							</div>
						</div>
					</div>
				</div>
			</fieldset>
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={stockAdjustment?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
			<ItemsError
				apiErrors={formErrors}
				formErrors={formState.errors}
				register={form.register}
				watch={form.watch}
				setErrors={setErrors}
			/>
			<StockAdjustmentItemsTable
				form={form}
				formErrors={formErrors}
				fetchStockAdjustment={fetchStockAdjustment}
			/>
			<h5>{t("lib:common.word.advanced")}</h5>
			<div>
				<TagsFormInput form={form} />
				<span className="mt-4 d-block">{t("common.word.attachments")}</span>
				<AttachmentsForm form={form} />
			</div>
		</FormDirty>
	);
};

export default StockAdjustmentForm;
